import {
  InspectionAddonCard,
  isInspectionEligible,
} from "@/components/booking/create/InspectionAddonCard";
import { TripEstimateView } from "@/components/booking/create/TripEstimateView";
import { CreateRequestLayout } from "@/components/customer/CreateRequestLayout";
import { ThemedText } from "@/components/themed-text";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { getApiErrorMessage } from "@/lib/api";
import { buildBookingPayload } from "@/lib/build-booking-payload";
import { isAxiosError } from "@/lib/errors";
import bookingService from "@/lib/services/bookings";
import { showToast } from "@/lib/toast";
import { isLocationOnlyService } from "@/lib/validation/booking";
import { useCreateRequestStore } from "@/stores/use-create-request-store";
import Ionicons from "@react-native-vector-icons/ionicons";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "expo-router";
import React, { useState } from "react";
import { Modal, StyleSheet, TouchableOpacity, View } from "react-native";

export default function SummaryScreen() {
  const router = useRouter();
  const { theme } = useAuth();
  const { formData, resetForm } = useCreateRequestStore();
  const [creating, setCreating] = useState(false);
  const [activeBookingModalVisible, setActiveBookingModalVisible] =
    useState(false);

  const serviceType = formData.service_type;
  const locationOnly = isLocationOnlyService(serviceType);
  const canEstimate = !!formData.from_latitude && !!formData.from_longitude;

  const {
    data: estimate = null,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ["booking-estimate", formData],
    enabled: canEstimate,
    retry: 0,
    queryFn: async () => {
      try {
        return await bookingService.bookingEstimate(
          buildBookingPayload(formData, { includeVehicleWeight: true }),
        );
      } catch (error: unknown) {
        showToast.error(
          getApiErrorMessage(error, "Failed to get price estimate"),
        );
        throw error;
      }
    },
  });

  const handleGetEstimate = () => {
    if (!canEstimate) {
      showToast.error("Pickup location is missing");
      return;
    }
    refetch();
  };

  const handleCreateBooking = async () => {
    if (!estimate) {
      showToast.warning("Please wait for the estimate");
      return;
    }

    try {
      setCreating(true);
      const booking = await bookingService.createBooking(
        buildBookingPayload(formData),
      );

      showToast.success("Booking request submitted!");

      setTimeout(() => {
        router.dismissAll();
        if(booking.scheduled_at) {
          router.push(`/bookings/${booking.id}`);
        } else {
          router.push(`/bookings/${booking.id}/search-driver`);
        }
        resetForm();
      }, 1500);
    } catch (error: unknown) {
      showToast.error(getApiErrorMessage(error, "Failed to create booking"));
      if (
        isAxiosError(error) &&
        error.response?.data?.error === "active_booking"
      ) {
        setActiveBookingModalVisible(true);
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <CreateRequestLayout
        step={5}
        title={locationOnly ? "Cost" : "Trip Estimate"}
        subtitle={
          locationOnly
            ? "Based on the options selected"
            : "Based on your route and vehicle"
        }
        onNext={handleCreateBooking}
        nextLabel={
          formData.scheduled_at &&
          new Date(formData.scheduled_at).setHours(0, 0, 0, 0) >
            new Date().setHours(0, 0, 0, 0)
            ? "Book Service"
            : "Request Service Now"
        }
        isNextLoading={creating}
        isNextDisabled={loading || !estimate}
      >
        <TripEstimateView
          loading={loading}
          estimate={estimate}
          locationOnly={locationOnly}
          fromAddress={formData.from_address}
          toAddress={formData.to_address}
          onRetry={handleGetEstimate}
          addon={
            isInspectionEligible(serviceType) ? (
              <InspectionAddonCard
                serviceType={serviceType}
                inspectionRequired={formData.inspection_required}
                inspectionTier={formData.inspection_tier}
                onChange={(values) =>
                  useCreateRequestStore.getState().setFormData(values)
                }
              />
            ) : null
          }
        />
      </CreateRequestLayout>

      <Modal
        visible={activeBookingModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setActiveBookingModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View
            style={[styles.modalContent, { backgroundColor: theme.background }]}
          >
            <View style={styles.modalHeader}>
              <Ionicons
                name="information-circle"
                size={40}
                color={BrandColors.primary}
              />
              <ThemedText style={styles.modalTitle} type="subtitle">
                Active booking request
              </ThemedText>
            </View>

            <ThemedText style={styles.modalDescription}>
              You already have an active booking request. Please wait for it to
              be completed or cancelled before creating a new one.
            </ThemedText>

            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.secondaryButton]}
                onPress={() => {
                  setActiveBookingModalVisible(false);
                  resetForm();
                  router.dismissAll();
                  router.push("/");
                }}
              >
                <ThemedText style={styles.secondaryButtonText}>
                  Go Home
                </ThemedText>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.modalButton, styles.primaryButton]}
                onPress={() => {
                  setActiveBookingModalVisible(false);
                  resetForm();
                  router.dismissAll();
                  router.push("/bookings?status=active");
                }}
              >
                <ThemedText style={styles.primaryButtonText}>
                  Go to Booking
                </ThemedText>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    padding: 20,
  },
  modalContent: {
    borderRadius: 20,
    padding: 24,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 5,
  },
  modalHeader: {
    alignItems: "center",
    marginBottom: 16,
  },
  modalTitle: {
    marginTop: 8,
    textAlign: "center",
  },
  modalDescription: {
    textAlign: "center",
    marginBottom: 24,
    color: BrandColors.mediumGray,
    lineHeight: 22,
  },
  modalButtons: {
    gap: 12,
  },
  modalButton: {
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: "center",
  },
  primaryButton: {
    backgroundColor: BrandColors.primary,
  },
  primaryButtonText: {
    color: "#fff",
    fontWeight: "600",
  },
  secondaryButton: {
    borderWidth: 1,
    borderColor: BrandColors.lightGray,
  },
  secondaryButtonText: {
    fontWeight: "600",
  },
});
